import { useDisclosure } from "@heroui/react";
import { ModalBuilder, ModalError, ModalField } from "../modalBuilder";
import { Agreement } from "@/api/agreements";
import { toast } from "react-toastify";
import { api } from "@/api";
import { getIdFromRenterHandbook } from "@/utils/getIdFromHandbooks.ts";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { normalizeDatetime } from "@/utils";
import { NewDepositeHandbook } from "../../handbooks/paymentHandbook";
import { CodependentRenterRequisitesHandbook } from "../../handbooks/renterRequisitesHandbook";

interface EditAgreementForm {
  number: string;
  renterId: number;
  requisiteId: string;
  renterRequisiteId: string;
  description: string;
  startDate: string;
  endDate: string;
  deposit: number;
  meetingHours: number;
  scan?: string;
}

const editAgreementFields: ModalField[] = [
  {
    label: "Номер договора",
    name: "number",
    placeholder: "0",
    type: "text",
    isRequired: false,
  },
  {
    label: "Арендатор",
    name: "renter",
    placeholder: "Арендатор",
    type: "renter",
    isRequired: true,
  },
  {
    label: "Счёт арендатора",
    name: "renterRequisite",
    placeholder: "Описание",
    type: "codependent",
    deps: ["renter"],
    element: CodependentRenterRequisitesHandbook,
    isRequired: true,
  },
  {
    label: "Счёт",
    name: "requisite",
    placeholder: "Описание",
    type: "requisites",
    isRequired: true,
  },
  {
    label: "Дата начала",
    name: "startDate",
    placeholder: "Дата начала",
    type: "datetime-local",
    isRequired: true,
  },
  {
    label: "Дата окончания",
    name: "endDate",
    placeholder: "Дата окончания",
    type: "datetime-local",
    isRequired: true,
  },
  {
    label: "Депозит",
    name: "deposit",
    placeholder: "Депозит",
    type: "codependent",
    deps: ["renter"],
    element: NewDepositeHandbook,
    isRequired: false,
  },
  {
    label: "Часы на переговорки",
    name: "meetingHours",
    placeholder: "0",
    type: "text",
    isRequired: true,
  },
  {
    label: "Описание",
    name: "description",
    placeholder: "Описание",
    type: "text",
    isRequired: false,
  },
  {
    label: "Скан договора",
    name: "scan",
    placeholder: "Скан договора",
    type: "file",
    isRequired: false,
  },
];

export function EditAgreementModal({
  disclosure,
  office,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  office: Agreement | null;
}) {
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);

  const handleError = useErrorHandling();

  const onSubmit = (data: EditAgreementForm) => {
    return new Promise<boolean>(async (resolve, _reject) => {
      const renter = await getIdFromRenterHandbook(String(data.renterId));

      // Сохраняем существующие offices и workplaces
      const currentAgreement = office!;

      api.agreements
        .update(office!.id, {
          number: data.number,
          offices: currentAgreement.offices || [],
          renterId: Number(renter),
          description: data.description,
          requisiteId: Number(data.requisiteId) || null,
          startDate: data.startDate,
          endDate: data.endDate,
          deposit: data.deposit,
          renterRequisiteId: Number(data.renterRequisiteId) || null,
          meetingHours: data.meetingHours,
          scan: data.scan,
        })
        .then((_data) => {
          loadHandbooks({ handbooks: ["agreements"] });
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>(async (resolve, _reject) => {
      api.agreements
        .delete(office!.id)
        .then((_data) => {
          loadHandbooks({ handbooks: ["agreements"] });
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <ModalBuilder
      title="Редактирование договора аренды"
      fields={editAgreementFields}
      isOpen={disclosure.isOpen}
      onOpenChange={disclosure.onOpenChange}
      modalErrors={modalErrors}
      onSubmit={onSubmit}
      onDelete={onDelete}
      isSensitiveData={true}
      submitButtonText="Сохранить"
      defaultValues={{
        number: office?.number,
        renterId: office?.renterId,
        description: office?.description,
        requisite: office?.requisiteId,
        start_date: normalizeDatetime(office?.startDate),
        end_date: normalizeDatetime(office?.endDate),
        deposit: office?.deposit,
        renterRequisiteId: office?.renterRequisiteId,
        meetingHours: office?.meetingHours,
        scan: office?.scan,
      }}
    />
  );
}
